import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Add from '@mui/icons-material/Add';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import Remove from '@mui/icons-material/Remove';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  IconButton,
  InputBase,
  Stack,
  Typography
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';

import { ESignFieldOverlay } from '@/components/ESign/ESignFieldOverlay';
import { ESignPdfViewer } from '@/components/ESign/ESignPdfViewer';
import { getSafeESignDocumentUrl } from '@/components/ESign/getSafeESignDocumentUrl';
import type { ESignPageDimensions } from '@/components/ESign/types';
import DetailsHeaderContainer from '@/components/ui/DetailsHeaderContainer/DetailsHeaderContainer';

import {
  EsignFieldKind,
  type PublicSignerSessionResponse,
  type PublicSignerSignatureValue
} from '../publicSignerTypes';
import { isFieldComplete } from '../utils/fieldDraft';
import { getOrderedEditableFields } from '../utils/fieldOrdering';
import {
  getCheckboxFieldValue,
  getSignatureFieldValue,
  getTextFieldValue,
  isFieldEditable,
  isTruthyFieldValue,
  toFieldPosition
} from '../utils/fieldValue';
import {
  formatExpiryCountdown,
  getFieldProgressLabel
} from '../utils/presentation';
import type {
  PublicSignerFieldDraftValue,
  PublicSignerFieldDraftValues,
  PublicSignerFieldErrorMap
} from '../utils/types';

import { PublicSignerDisclosureGate } from './PublicSignerDisclosureGate';
import { PublicSignerOverlayButton } from './PublicSignerOverlayButton';
import { PublicSignerSignatureDialog } from './PublicSignerSignatureDialog';
import { PublicSignerSignaturePreview } from './PublicSignerSignaturePreview';
import { PublicSignerSummaryItem } from './PublicSignerSummaryItem';

const BASE_PDF_WIDTH = 1120;
const DEFAULT_PDF_ZOOM = 100;
const MIN_PDF_ZOOM = 50;
const MAX_PDF_ZOOM = 200;
const PDF_ZOOM_STEP = 10;
const MAX_TEXT_FIELD_LENGTH = 500;

export function PublicSignerWorkspace({
  fieldDraftValues,
  fieldErrors,
  isSubmitting,
  lastAppliedSignatureMode,
  lastDrawnImageDataUrl,
  lastTypedName,
  onAdvanceToNextField,
  onAdvanceToPreviousField,
  onContinueDisclosure,
  onFieldDraftChange,
  onOpenSignatureField,
  onRefetchSession,
  onSelectField,
  onSignatureApplied,
  onSubmit,
  openSignatureFieldId,
  requiresDisclosure,
  selectedFieldId,
  selectedFieldScrollRequestId,
  session
}: {
  fieldDraftValues: PublicSignerFieldDraftValues;
  fieldErrors: PublicSignerFieldErrorMap;
  isSubmitting: boolean;
  lastAppliedSignatureMode: 'typed' | 'drawn' | null;
  lastDrawnImageDataUrl: string | null;
  lastTypedName: string | null;
  onAdvanceToNextField: () => void;
  onAdvanceToPreviousField: () => void;
  onContinueDisclosure: () => void;
  onFieldDraftChange: (
    fieldId: string,
    value: PublicSignerFieldDraftValue
  ) => void;
  onOpenSignatureField: (fieldId: string | null) => void;
  onRefetchSession: () => Promise<unknown>;
  onSelectField: (fieldId: string) => void;
  onSignatureApplied: (value: PublicSignerSignatureValue | null) => void;
  onSubmit: () => Promise<void>;
  openSignatureFieldId: string | null;
  requiresDisclosure: boolean;
  selectedFieldId: string | null;
  selectedFieldScrollRequestId: number;
  session: PublicSignerSessionResponse;
}) {
  const theme = useTheme();
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const orderedEditableFields = useMemo(
    () => getOrderedEditableFields(session.fields),
    [session.fields]
  );
  const [containerWidth, setContainerWidth] = useState(BASE_PDF_WIDTH);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [pdfZoom, setPdfZoom] = useState(DEFAULT_PDF_ZOOM);
  const previewUrl = getSafeESignDocumentUrl(session.document_url);
  const selectedField = useMemo(
    () => session.fields.find((field) => field.id === selectedFieldId) ?? null,
    [selectedFieldId, session.fields]
  );
  const signatureDialogField = useMemo(
    () =>
      session.fields.find(
        (field) =>
          field.id === openSignatureFieldId &&
          field.field_kind === EsignFieldKind.Signature
      ) ?? null,
    [openSignatureFieldId, session.fields]
  );
  const completedEditableFieldCount = useMemo(
    () =>
      orderedEditableFields.filter((field) =>
        isFieldComplete(field, fieldDraftValues[field.id])
      ).length,
    [fieldDraftValues, orderedEditableFields]
  );
  const fieldProgressLabel = useMemo(
    () =>
      getFieldProgressLabel(
        completedEditableFieldCount,
        orderedEditableFields.length
      ),
    [completedEditableFieldCount, orderedEditableFields.length]
  );
  const fitPdfWidth = useMemo(
    () => Math.min(Math.max(containerWidth, 480), BASE_PDF_WIDTH),
    [containerWidth]
  );
  const pdfWidth = useMemo(
    () => Math.round((fitPdfWidth * pdfZoom) / 100),
    [fitPdfWidth, pdfZoom]
  );
  const truncatableSection = useCallback(
    (maxWidth = 240) => ({
      flex: '0 1 auto',
      maxWidth,
      minWidth: 90,
      overflow: 'hidden'
    }),
    []
  );
  const fixedCell = useMemo(
    () => ({ flex: '0 0 auto', minWidth: 'max-content' }),
    []
  );

  useEffect(() => {
    if (!pdfContainerRef.current) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    observer.observe(pdfContainerRef.current);

    return () => observer.disconnect();
  }, [previewUrl]);

  useEffect(() => {
    const container = pdfContainerRef.current;
    if (!container) {
      return;
    }

    const updateCurrentPage = () => {
      const pages = container.querySelectorAll('.react-pdf__Page');
      if (pages.length === 0) {
        return;
      }
      const containerRect = container.getBoundingClientRect();
      const containerCenter = containerRect.top + containerRect.height / 2;
      let closestPage = 1;
      let closestDistance = Number.POSITIVE_INFINITY;
      pages.forEach((page) => {
        const rect = page.getBoundingClientRect();
        const distance = Math.abs(rect.top + rect.height / 2 - containerCenter);
        const pageNum = Number(page.getAttribute('data-page-number'));
        if (distance < closestDistance && pageNum) {
          closestDistance = distance;
          closestPage = pageNum;
        }
      });
      setCurrentPage(closestPage);
    };

    container.addEventListener('scroll', updateCurrentPage, { passive: true });

    return () => {
      container.removeEventListener('scroll', updateCurrentPage);
    };
  }, [previewUrl, numPages]);

  useEffect(() => {
    if (!selectedFieldId || requiresDisclosure) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      document
        .querySelector<HTMLElement>(
          `[data-public-signer-field-id="${selectedFieldId}"]`
        )
        ?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [requiresDisclosure, selectedFieldId, selectedFieldScrollRequestId]);

  const renderOverlay = useCallback(
    (pageNumber: number, dimensions: ESignPageDimensions) => {
      const pageFields = session.fields.filter(
        (field) => field.page_index + 1 === pageNumber
      );

      if (pageFields.length === 0) {
        return null;
      }

      return (
        <>
          {pageFields.map((field) => {
            const draftValue = fieldDraftValues[field.id];
            const isSelected = selectedFieldId === field.id;
            const fieldHasError = Boolean(fieldErrors[field.id]);
            const color = fieldHasError
              ? theme.palette.error.main
              : isFieldEditable(field)
                ? theme.palette.primary.main
                : theme.palette.text.secondary;
            const isCheckbox = field.field_kind === EsignFieldKind.Checkbox;

            let content: ReactNode;

            if (!isFieldEditable(field)) {
              if (field.field_kind === EsignFieldKind.Signature) {
                content = (
                  <PublicSignerSignaturePreview
                    placeholder={field.label}
                    value={getSignatureFieldValue(field.value)}
                  />
                );
              } else if (field.field_kind === EsignFieldKind.Checkbox) {
                content = isTruthyFieldValue(field.value) ? (
                  <CheckRoundedIcon sx={{ fontSize: 18 }} />
                ) : null;
              } else {
                content = (
                  <Typography
                    noWrap
                    sx={{ fontSize: 10, fontWeight: 600, maxWidth: '100%' }}
                  >
                    {getTextFieldValue(field.value).trim() || field.label}
                  </Typography>
                );
              }
            } else if (field.field_kind === EsignFieldKind.Checkbox) {
              const isChecked = getCheckboxFieldValue(draftValue);

              content = (
                <PublicSignerOverlayButton
                  ariaChecked={isChecked}
                  ariaLabel={field.label}
                  errorMessage={fieldErrors[field.id]}
                  fieldId={field.id}
                  hasError={fieldHasError}
                  onClick={() => {
                    onSelectField(field.id);
                    onFieldDraftChange(field.id, !isChecked);
                  }}
                  role="checkbox"
                >
                  {isChecked ? (
                    <CheckRoundedIcon sx={{ fontSize: 18 }} />
                  ) : null}
                </PublicSignerOverlayButton>
              );
            } else if (field.field_kind === EsignFieldKind.Signature) {
              content = (
                <PublicSignerOverlayButton
                  ariaLabel={field.label}
                  errorMessage={fieldErrors[field.id]}
                  fieldId={field.id}
                  hasError={fieldHasError}
                  onClick={() => {
                    onSelectField(field.id);
                    onOpenSignatureField(field.id);
                  }}
                >
                  <PublicSignerSignaturePreview
                    placeholder={field.label}
                    value={getSignatureFieldValue(draftValue)}
                  />
                </PublicSignerOverlayButton>
              );
            } else if (isSelected) {
              const fieldErrorId = fieldHasError
                ? `public-signer-field-error-${field.id}`
                : undefined;
              content = (
                <>
                  <InputBase
                    data-public-signer-field-id={field.id}
                    inputRef={(el: HTMLInputElement | null) => {
                      el?.focus({ preventScroll: true });
                    }}
                    inputProps={{
                      'aria-describedby': fieldErrorId,
                      'aria-label': field.label,
                      'aria-invalid': fieldHasError || undefined,
                      maxLength: MAX_TEXT_FIELD_LENGTH,
                      type: 'text'
                    }}
                    onChange={(event) => {
                      onFieldDraftChange(field.id, event.target.value);
                    }}
                    onFocus={() => {
                      onSelectField(field.id);
                    }}
                    sx={{
                      color: 'text.primary',
                      fontSize: 12,
                      fontWeight: 600,
                      height: '100%',
                      px: 0.75,
                      width: '100%',
                      ...(fieldHasError
                        ? {
                            backgroundColor: alpha(
                              theme.palette.error.main,
                              0.08
                            )
                          }
                        : undefined),
                      '& input': {
                        p: 0,
                        textAlign: 'center'
                      }
                    }}
                    value={getTextFieldValue(draftValue)}
                  />
                  {fieldErrorId ? (
                    <Box component="span" id={fieldErrorId} sx={visuallyHidden}>
                      {fieldErrors[field.id]}
                    </Box>
                  ) : null}
                </>
              );
            } else {
              const displayValue =
                getTextFieldValue(draftValue).trim() || field.label;

              content = (
                <PublicSignerOverlayButton
                  ariaLabel={field.label}
                  errorMessage={fieldErrors[field.id]}
                  fieldId={field.id}
                  hasError={fieldHasError}
                  onClick={() => {
                    onSelectField(field.id);
                  }}
                >
                  <Typography
                    noWrap
                    sx={{ fontSize: 10, fontWeight: 600, maxWidth: '100%' }}
                  >
                    {displayValue}
                  </Typography>
                </PublicSignerOverlayButton>
              );
            }

            return (
              <ESignFieldOverlay
                color={color}
                hoverContent={
                  isFieldEditable(field)
                    ? `Assigned to you • Page ${field.page_index + 1}`
                    : `Read only • Page ${field.page_index + 1}`
                }
                isCheckbox={isCheckbox}
                isSelected={isSelected}
                key={field.id}
                pageDimensions={dimensions}
                position={toFieldPosition(field)}
                style={{
                  alignItems: 'center',
                  backgroundColor: isCheckbox
                    ? getCheckboxFieldValue(draftValue)
                      ? alpha(color, 0.14)
                      : 'transparent'
                    : fieldHasError
                      ? alpha(theme.palette.error.main, 0.12)
                      : alpha(color, 0.1),
                  border: `${fieldHasError ? 2 : 1}px solid ${color}`,
                  boxShadow: fieldHasError
                    ? `0 0 0 3px ${alpha(theme.palette.error.main, 0.14)}`
                    : undefined,
                  borderRadius: isCheckbox ? 0 : 6,
                  color: theme.palette.text.primary,
                  display: 'flex',
                  fontWeight: 600,
                  justifyContent: 'center',
                  padding: 0,
                  textAlign: 'center',
                  userSelect: 'none'
                }}
              >
                {content}
              </ESignFieldOverlay>
            );
          })}
        </>
      );
    },
    [
      fieldDraftValues,
      fieldErrors,
      onFieldDraftChange,
      onOpenSignatureField,
      onSelectField,
      selectedFieldId,
      session.fields,
      theme.palette.error.main,
      theme.palette.primary.main,
      theme.palette.text.primary,
      theme.palette.text.secondary
    ]
  );

  if (requiresDisclosure) {
    return (
      <PublicSignerDisclosureGate
        onContinue={onContinueDisclosure}
        session={session}
      />
    );
  }

  return (
    <Container
      maxWidth={false}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        px: { xs: 1, md: 2 },
        py: { xs: 2, md: 3 }
      }}
    >
      <Stack gap={1.5} sx={{ flex: 1, minHeight: 0 }}>
        <DetailsHeaderContainer
          sx={(theme) => ({
            border: `1px solid ${theme.palette.divider}`,
            gap: '12px',
            mb: 0,
            overflowX: 'auto',
            py: 0,
            width: '100%',
            '.label': {
              maxHeight: 32,
              minWidth: 0,
              overflow: 'hidden',
              textAlign: 'left',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            },
            '.sub-label': {
              height: 18,
              whiteSpace: 'nowrap'
            }
          })}
        >
          <Box sx={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden' }}>
            <Stack
              direction="row"
              spacing={1.5}
              sx={{ flexWrap: 'nowrap', minWidth: 'max-content' }}
            >
              <Box sx={truncatableSection(280)}>
                <PublicSignerSummaryItem
                  label="Document"
                  value={session.request_title?.trim() || 'Signature request'}
                />
              </Box>
              {session.facility_name?.trim() ? (
                <Box sx={truncatableSection(220)}>
                  <PublicSignerSummaryItem
                    label="Facility"
                    value={session.facility_name.trim()}
                  />
                </Box>
              ) : null}
              <Box sx={truncatableSection(220)}>
                <PublicSignerSummaryItem
                  label="Signer name"
                  value={session.signer_name?.trim() || 'Signer'}
                />
              </Box>
              <Box sx={truncatableSection(220)}>
                <PublicSignerSummaryItem
                  label="Signer role"
                  value={session.signer_role?.trim() || 'Signer'}
                />
              </Box>
              <Box sx={fixedCell}>
                <PublicSignerSummaryItem
                  label="Link expires in"
                  value={formatExpiryCountdown(session.session_expires_at)}
                />
              </Box>
              <Box sx={fixedCell}>
                <PublicSignerSummaryItem
                  label="Fields"
                  value={
                    <Chip
                      label={fieldProgressLabel}
                      size="small"
                      sx={{
                        backgroundColor:
                          completedEditableFieldCount >=
                            orderedEditableFields.length &&
                          orderedEditableFields.length > 0
                            ? 'success.main'
                            : 'warning.main',
                        color: 'primary.contrastText',
                        width: 'fit-content',
                        '& .MuiChip-label': {
                          fontSize: 12,
                          fontWeight: 700
                        }
                      }}
                    />
                  }
                />
              </Box>
            </Stack>
          </Box>
          <Stack
            alignItems="center"
            direction="row"
            spacing={1}
            sx={{ flex: '0 0 auto', ml: 'auto' }}
          >
            <LoadingButton
              disabled={
                completedEditableFieldCount < orderedEditableFields.length
              }
              loading={isSubmitting}
              onClick={async () => {
                await onSubmit();
              }}
              variant="contained"
            >
              Complete signing
            </LoadingButton>
          </Stack>
        </DetailsHeaderContainer>

        <Box
          sx={(theme) => ({
            backgroundColor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '20px',
            display: 'flex',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden'
          })}
        >
          <Stack spacing={0} sx={{ flex: 1, minHeight: 0 }}>
            <Stack
              alignItems={{ xs: 'flex-start', md: 'center' }}
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              spacing={1.5}
              sx={(theme) => ({
                borderBottom: `1px solid ${theme.palette.divider}`,
                px: 2,
                py: 1.5
              })}
            >
              <Stack minWidth={0} spacing={0.25}>
                <Typography color="text.secondary" variant="caption">
                  Active field
                </Typography>
                <Typography
                  color="text.primary"
                  noWrap
                  sx={{
                    fontSize: 16,
                    fontWeight: 600,
                    maxWidth: { xs: '100%', md: 320 }
                  }}
                  variant="body2"
                >
                  {selectedField?.label ?? 'No field selected'}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                flexWrap="wrap"
                gap={1}
                justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
              >
                <Chip
                  label={`Page ${currentPage} of ${numPages || '—'}`}
                  size="small"
                  sx={(theme) => ({
                    backgroundColor: alpha(theme.palette.info.main, 0.7),
                    color: theme.palette.text.secondary,
                    '& .MuiChip-label': {
                      fontSize: 12,
                      fontWeight: 600
                    }
                  })}
                  variant="filled"
                />
                <Chip
                  label={`${completedEditableFieldCount}/${orderedEditableFields.length} complete`}
                  size="small"
                  sx={(theme) => ({
                    backgroundColor:
                      completedEditableFieldCount >=
                        orderedEditableFields.length &&
                      orderedEditableFields.length > 0
                        ? alpha(theme.palette.success.main, 0.12)
                        : alpha(theme.palette.info.main, 0.7),
                    color:
                      completedEditableFieldCount >=
                        orderedEditableFields.length &&
                      orderedEditableFields.length > 0
                        ? theme.palette.success.main
                        : theme.palette.text.secondary,
                    '& .MuiChip-label': {
                      fontSize: 12,
                      fontWeight: 600
                    }
                  })}
                  variant="filled"
                />
              </Stack>
            </Stack>

            <Stack
              alignItems="center"
              direction="row"
              justifyContent="flex-end"
              spacing={1}
              sx={(theme) => ({
                borderBottom: `1px solid ${theme.palette.divider}`,
                px: 2,
                py: 0.75
              })}
            >
              <Stack alignItems="center" direction="row" spacing={0.25}>
                <IconButton
                  aria-label="Zoom out document"
                  disabled={pdfZoom <= MIN_PDF_ZOOM}
                  onClick={() => {
                    setPdfZoom((currentZoom) =>
                      Math.max(MIN_PDF_ZOOM, currentZoom - PDF_ZOOM_STEP)
                    );
                  }}
                  size="small"
                  sx={{ color: 'text.secondary' }}
                >
                  <Remove sx={{ fontSize: 16 }} />
                </IconButton>
                <Typography
                  sx={{
                    color: 'text.secondary',
                    fontSize: 12,
                    fontWeight: 500,
                    minWidth: 44,
                    textAlign: 'center'
                  }}
                  variant="body2"
                >
                  {pdfZoom}%
                </Typography>
                <IconButton
                  aria-label="Zoom in document"
                  disabled={pdfZoom >= MAX_PDF_ZOOM}
                  onClick={() => {
                    setPdfZoom((currentZoom) =>
                      Math.min(MAX_PDF_ZOOM, currentZoom + PDF_ZOOM_STEP)
                    );
                  }}
                  size="small"
                  sx={{ color: 'text.secondary' }}
                >
                  <Add sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>
            </Stack>

            {previewUrl ? (
              <Box
                ref={pdfContainerRef}
                sx={(theme) => ({
                  backgroundColor: theme.palette.info.main,
                  flex: 1,
                  minHeight: 0,
                  overflow: 'auto',
                  p: { xs: 1, md: 1.5 }
                })}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%'
                  }}
                >
                  <ESignPdfViewer
                    onDocumentLoad={(pages) => {
                      setCurrentPage(1);
                      setNumPages(pages);
                    }}
                    onUrlExpired={async () => {
                      await onRefetchSession();
                    }}
                    renderOverlay={renderOverlay}
                    url={previewUrl}
                    width={pdfWidth}
                  />
                </Box>
              </Box>
            ) : (
              <Stack p={2}>
                <Alert severity="info">
                  The preview document is not available yet for this signing
                  session.
                </Alert>
              </Stack>
            )}
          </Stack>
        </Box>
      </Stack>

      <PublicSignerSignatureDialog
        error={
          signatureDialogField
            ? fieldErrors[signatureDialogField.id]
            : undefined
        }
        field={signatureDialogField}
        initialValue={
          signatureDialogField
            ? getSignatureFieldValue(fieldDraftValues[signatureDialogField.id])
            : null
        }
        presetDrawnImageDataUrl={lastDrawnImageDataUrl}
        presetMode={lastAppliedSignatureMode}
        presetTypedName={lastTypedName}
        onClose={() => {
          onOpenSignatureField(null);
        }}
        onSave={(value) => {
          if (!signatureDialogField) {
            return;
          }

          onFieldDraftChange(signatureDialogField.id, value);
          onSignatureApplied(value);
          onOpenSignatureField(null);
          onSelectField(signatureDialogField.id);
        }}
      />

      {orderedEditableFields.length > 1 ? (
        <Stack
          direction="row"
          spacing={2}
          sx={{
            bottom: { xs: 16, md: 24 },
            position: 'fixed',
            right: { xs: 16, md: 24 },
            zIndex: (theme) => theme.zIndex.speedDial
          }}
        >
          <Button
            color="secondary"
            onClick={onAdvanceToPreviousField}
            variant="outlined"
          >
            Previous field
          </Button>
          <Button
            color="primary"
            onClick={onAdvanceToNextField}
            variant="contained"
          >
            Next field
          </Button>
        </Stack>
      ) : null}
    </Container>
  );
}
